

import React, { useState, useEffect } from "react";
import { activityService } from "../services/activityService";
import "../styles/my-activity.css";
import { Button, TextField, Autocomplete, Popper, IconButton, Tooltip, InputAdornment, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import AssignActivityIcon from '../components/AssignActivityIcon';
import ViewActivityTasksModal from "../components/ViewActivityTasksModal";
import UpdateActivityModal from "../components/UpdateActivityModal";
import { benchActivityService, MyActivityTaskDto } from "../services/benchActivityService";
import AssignActivityModal from "../components/assign/AssignActivityModal";
import { apiGet, apiPut } from "../api/http";
import { dateFormat } from '../utils/dateFormat';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';

// Banner type for toast messages
type BannerType = { type: "success" | "error"; message: string } | null;

type LocalIconProps = {
    src: string;
    alt?: string;
    className?: string;
};

const LocalIcon: React.FC<LocalIconProps> = ({ src, alt = "", className }) => (
    <img
        src={src}
        alt={alt}
        className={className}
        loading="lazy"
    />
);

const MyActivity: React.FC = () => {
    const [banner, setBanner] = useState<BannerType>(null);
        // Auto-dismiss banner after 3s
        useEffect(() => {
            if (banner) {
                const t = setTimeout(() => setBanner(null), 3000);
                return () => clearTimeout(t);
            }
        }, [banner]);
    // Filtered activities for grid display
    const [activities, setActivities] = useState<any[]>([]);
    // All activities for the current week (unfiltered, for assignment logic)
    const [allWeekActivities, setAllWeekActivities] = useState<any[]>([]);
    // Determine if the current user is benched in the selected (current) week.
    const isUserBenched = React.useMemo(() => {
        return (allWeekActivities || []).some((a: any) => Number(a.benchId) > 0);
    }, [allWeekActivities]);
    // Holds all assigned activities for the current week, regardless of filters
    const [allAssignedActivities, setAllAssignedActivities] = useState<any[]>([]);
    const [filters, setFilters] = useState({
    weekStartDate: "",

    category: null as number | null,
    
    technology: null as number | null,

    status: "",

    global: "",
});
    // Controlled input for persistent X icon in filters
    const [weekInput, setWeekInput] = useState("");
    const [categoryInput, setCategoryInput] = useState("");
    const [technologyInput, setTechnologyInput] = useState("");
    const [statusInput, setStatusInput] = useState("");
    const [weeks, setWeeks] = useState<{ id: number; weekNumber: string; startDate: string }[]>([]);
    const [weeksInitialized, setWeeksInitialized] = useState(false);
    const [categories, setCategories] = useState<any[]>([]);
    const [technologies, setTechnologies] = useState<any[]>([]);
    const [statuses, setStatuses] = useState<any[]>([]);
    const [allActivities, setAllActivities] = useState<any[]>([]);
    const [activityVersion, setActivityVersion] = useState(0);
    const [employees, setEmployees] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [sortColumn, setSortColumn] = useState<string>('weekStartDate');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
    const [updateModalOpen, setUpdateModalOpen] = useState(false);
    const [updateModalData, setUpdateModalData] = useState<any | null>(null);
    const [assignModalOpen, setAssignModalOpen] = useState(false);
    const [assigningBenchId, setAssigningBenchId] = useState<number | null>(null);
    // Confirmation dialog for reassigning completed activity
    const [reassignConfirm, setReassignConfirm] = useState({ open: false, week: '', activity: null as any, payload: null as any, benchId: null as number | null });
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [viewModalTasks, setViewModalTasks] = useState<any[]>([]);
    const [viewModalActivityName, setViewModalActivityName] = useState<string>("");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const fetchActivitiesRequestIdRef = React.useRef(0);

    const gridCategories = React.useMemo(() => {
        const ids = new Set(activities.map((a) => a.categoryId));
        return categories
            .filter((c) => ids.has(c.id))
            .sort((a, b) => (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase()));
    }, [activities, categories]);

    const gridTechnologies = React.useMemo(() => {
        const ids = new Set(activities.map((a) => a.technologyId));
        return technologies
            .filter((t) => ids.has(t.id))
            .sort((a, b) => (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase()));
    }, [activities, technologies]);

    const gridStatuses = React.useMemo(() => {
        return Array.from(new Set(activities.map((a) => a.status)))
            .sort((x, y) => String(x || '').toLowerCase().localeCompare(String(y || '').toLowerCase()));
    }, [activities]);
    useEffect(() => {
        loadWeeks();
    }, []);

     const gridWeeks = React.useMemo(() => {
    return [...weeks].sort((a, b) => {
        const ta = Number(new Date(a.startDate || '').getTime()) || 0;
        const tb = Number(new Date(b.startDate || '').getTime()) || 0;
        return tb - ta; // latest first
    });
    }, [weeks]);

    const loadWeeks = async () => {
    try {
        const data: any[] = await apiGet("/bench/weeks");
        // Sort latest first so weeks[0] is always the current week
        const sorted = [...(data ?? [])].sort((a, b) =>
         new Date(b.startDate).getTime() - new Date(a.startDate).getTime()
            );
            setWeeks(sorted);
            if (sorted.length > 0) {
                // Auto-select the current week on page load
                const currentWeekStartDate = sorted[0].startDate?.slice(0, 10);
                if (currentWeekStartDate) {
                    setFilters(prev => ({ ...prev, weekStartDate: currentWeekStartDate }));
                }
            }
    } catch (err) {
        console.error("Week load error", err);
        setWeeks([]);
    } finally {
        setWeeksInitialized(true);
    }
};

    const handleFilterChange = (field: string, value: any) => {
        setFilters((prev) => ({ ...prev, [field]: value }));
    };

    useEffect(() => {
        const fetchFilters = async () => {
            try {
                setLoading(true);
                const [
                    categoriesData,
                    technologiesData,
                    employeesData,
                    allActivitiesData
                ]: any = await Promise.all([
                    activityService.getCategories(),
                    activityService.getTechnologies(),
                    activityService.getEmployees(),
                    // Pull a large page so active options are not dropped from modal autocomplete.
                    apiGet("/Activity?PageNumber=1&PageSize=10000")
                ]);
                setCategories(categoriesData);
                setTechnologies(technologiesData);
                setEmployees(
                    Array.isArray(employeesData)
                        ? employeesData
                        : employeesData?.data || []
                );
                setStatuses(['Yet to start', 'In Progress', 'Completed']);
                if (Array.isArray(allActivitiesData)) {
                    setAllActivities(allActivitiesData);
                } else if (allActivitiesData && typeof allActivitiesData === 'object' && 'items' in allActivitiesData && Array.isArray((allActivitiesData as any).items)) {
                    setAllActivities((allActivitiesData as any).items);
                } else {
                    setAllActivities([]);
                }
            } catch (e) {
                setError("Failed to load filters");
            } finally {
                setLoading(false);
            }
        };
        fetchFilters();
    }, [activityVersion]);

    const fetchActivities = async () => {
        setLoading(true);
        setError(null);
        const requestId = ++fetchActivitiesRequestIdRef.current;
        try {
            const params = [];
            if (filters.category) params.push(`categoryId=${filters.category}`);
            if (filters.technology) params.push(`technologyId=${filters.technology}`);
            if (filters.weekStartDate) params.push(`weekStartDate=${encodeURIComponent(filters.weekStartDate)}`);
            if (filters.status) params.push(`status=${encodeURIComponent(filters.status)}`);
            const query = params.length > 0 ? `?${params.join("&")}` : "";

            const response: any = await apiGet(`/my-activity/GetMyActivity${query}`);
            const raw = response?.data ?? response;
            const rows: any[] = Array.isArray(raw?.data) ? raw.data : Array.isArray(raw) ? raw : [];

            const detailedActivities = await Promise.all(
                rows.map(async (row: any) => {
                    let activityDetails: any = {};
                    try {
                        activityDetails = await activityService.getActivityById(row.activityId);
                    } catch {
                        activityDetails = {};
                    }
                    return {
                        category: activityDetails?.categoryName || "",
                        categoryId: Number(activityDetails?.categoryId ?? row.categoryId ?? 0),
                        technology: activityDetails?.technologyName || "",
                        technologyId: Number(activityDetails?.technologyId ?? row.technologyId ?? 0),
                        name: activityDetails?.name || "",
                        description: activityDetails?.description || "",
                        status: row.status,
                        startDate: row.startDate,
                        endDate: row.endDate,
                        hours: row.totalHours ? row.totalHours.toFixed(2) : "",
                        ...row,
                    };
                })
            );

            // Sort and pin logic (unchanged)
            detailedActivities.sort((a, b) => {
                const benchA = Number(a.benchId ?? 0);
                const benchB = Number(b.benchId ?? 0);
                if (benchA !== benchB) return benchB - benchA;
                const engA = Number(a.engagementId ?? 0);
                const engB = Number(b.engagementId ?? 0);
                return engB - engA;
            });
            try {
                const s = sessionStorage.getItem('pinnedBenchId');
                const pinId = s ? Number(s) : null;
                if (pinId && detailedActivities.length > 0) {
                    const pinIdx = detailedActivities.findIndex(a => Number(a.benchId) === pinId);
                    if (pinIdx > -1) {
                        const [pinned] = detailedActivities.splice(pinIdx, 1);
                        detailedActivities.unshift(pinned);
                        const prevActivities = activities;
                        if (prevActivities && prevActivities.length > 0) {
                            const rest = detailedActivities.slice(1);
                            rest.sort((a, b) => {
                                const prevIdxA = prevActivities.findIndex(p => Number(p.benchId) === Number(a.benchId));
                                const prevIdxB = prevActivities.findIndex(p => Number(p.benchId) === Number(b.benchId));
                                if (prevIdxA === -1 && prevIdxB === -1) return Number(b.benchId) - Number(a.benchId);
                                if (prevIdxA === -1) return -1;
                                if (prevIdxB === -1) return 1;
                                return prevIdxA - prevIdxB;
                            });
                            detailedActivities.splice(1, rest.length, ...rest);
                        }
                    }
                }
            } catch {}
            if (requestId !== fetchActivitiesRequestIdRef.current) return;
            setActivities(detailedActivities);
                    } catch (err: any) {
                        if (requestId !== fetchActivitiesRequestIdRef.current) return;
                        setError(err?.message || "Failed to fetch activities");
                        setActivities([]);
                    } finally {

                    if (requestId !== fetchActivitiesRequestIdRef.current) return;

                    // Fetch all activities for the current week (unfiltered, for assignment logic)
                    if (filters.weekStartDate) {
                        try {
                            const allWeekResp: any = await apiGet(`/my-activity/GetMyActivity?weekStartDate=${encodeURIComponent(filters.weekStartDate)}`);
                            const allWeekRaw = allWeekResp?.data ?? allWeekResp;
                            const allWeekRows: any[] = Array.isArray(allWeekRaw?.data) ? allWeekRaw.data : Array.isArray(allWeekRaw) ? allWeekRaw : [];
                            const allWeekDetailed = await Promise.all(
                                allWeekRows.map(async (row: any) => {
                                    let activityDetails: any = {};
                                    try {
                                        activityDetails = await activityService.getActivityById(row.activityId);
                                    } catch {
                                        activityDetails = {};
                                    }
                                    return {
                                        category: activityDetails?.categoryName || "",
                                        categoryId: Number(activityDetails?.categoryId ?? row.categoryId ?? 0),
                                        technology: activityDetails?.technologyName || "",
                                        technologyId: Number(activityDetails?.technologyId ?? row.technologyId ?? 0),
                                        name: activityDetails?.name || "",
                                        description: activityDetails?.description || "",
                                        status: row.status,
                                        startDate: row.startDate,
                                        endDate: row.endDate,
                                        hours: row.totalHours ? row.totalHours.toFixed(2) : "",
                                        ...row,
                                    };
                                })
                            );
                            if (requestId !== fetchActivitiesRequestIdRef.current) return;
                            setAllAssignedActivities(allWeekDetailed);
                            setAllWeekActivities(allWeekDetailed); // <-- for assignment logic
                        } catch {
                            if (requestId !== fetchActivitiesRequestIdRef.current) return;
                            setAllAssignedActivities([]);
                            setAllWeekActivities([]);
                        }
                    } 

                    else {

    setAllWeekActivities([]);
}
                    if (requestId === fetchActivitiesRequestIdRef.current) {
                        setLoading(false);
                    }
                }
            };

    useEffect(() => {
        if (!weeksInitialized) {
            return;
        }
        fetchActivities();
    }, [weeksInitialized, filters.category, filters.technology, filters.weekStartDate, filters.status, filters.global]);

    // Reset page on filter/search change — same as AssignActivity
    useEffect(() => { setPage(0); }, [filters.weekStartDate, filters.category, filters.technology, filters.status, filters.global]);

    // Enhanced filtered data for grid display (supports manual text input for filters)
    // Only filter grid by selected values, not by input text (matches AssignActivity behavior)
    const filteredData = activities.filter((row: any) => {
        const global = filters.global.trim().toLowerCase();
        const searchFields = [
            row.weekStartDate ? dateFormat(row.weekStartDate) : '',
            row.category,
            row.technology,
            row.name,
            row.status,
            row.startDate ? dateFormat(row.startDate) : '',
            row.endDate ? dateFormat(row.endDate) : '',
            row.hours
        ];

        
        const matchesGlobal =
            !global ||
            searchFields.some(val => val && val.toString().toLowerCase().includes(global));

        // Category filter: only by selected value
        const categoryMatch = !filters.category ? true : Number(row.categoryId) === Number(filters.category);
        // Technology filter: only by selected value
        const technologyMatch = !filters.technology ? true : Number(row.technologyId) === Number(filters.technology);
        // Status filter: only by selected value
        const statusMatch = !filters.status ? true : row.status?.toLowerCase() === filters.status.toLowerCase();

        return (
            (!filters.weekStartDate || row.weekStartDate?.slice(0, 10) === filters.weekStartDate) &&
            categoryMatch &&
            technologyMatch &&
            statusMatch &&
            matchesGlobal
        );
    });

    const sortableColumns = ["weekStartDate", "weekNumber", "week", "category", "technology", "name", "status", "startDate", "endDate"];
    const sortedData = [...filteredData].sort((a, b) => {
        // Always sort by weekStartDate descending if no week filter and no explicit sort column
        if (!filters.weekStartDate && (!sortColumn || sortColumn === 'weekStartDate' || sortColumn === 'week' || sortColumn === 'weekNumber')) {
            const dateA = new Date(a.weekStartDate || a.startDate || 0).getTime();
            const dateB = new Date(b.weekStartDate || b.startDate || 0).getTime();
            return sortDirection === 'asc' ? dateA - dateB : dateB - dateA;
        }
        if (!sortColumn || !sortableColumns.includes(sortColumn)) return 0;
        let valA = a[sortColumn];
        let valB = b[sortColumn];
        // Special handling for Week column (sort by weekStartDate as date)
        if (sortColumn === 'weekStartDate' || sortColumn === 'weekNumber' || sortColumn === 'week') {
            const dateA = new Date(a.weekStartDate || a.startDate || 0).getTime();
            const dateB = new Date(b.weekStartDate || b.startDate || 0).getTime();
            return sortDirection === 'asc' ? dateA - dateB : dateB - dateA;
        }
        if (valA == null) return 1;
        if (valB == null) return -1;
        if (String(sortColumn).toLowerCase().includes("date")) {
            return sortDirection === "asc"
                ? new Date(valA).getTime() - new Date(valB).getTime()
                : new Date(valB).getTime() - new Date(valA).getTime();
        }
        return sortDirection === "asc"
            ? String(valA).localeCompare(String(valB))
            : String(valB).localeCompare(String(valA));
    });

    // ── Pagination — identical logic to AssignActivity ───────────────────────
    const totalPages = Math.max(1, Math.ceil(sortedData.length / rowsPerPage));
    const isPrevDisabled = page === 0;
    const isNextDisabled = page >= totalPages - 1;
    const paginatedData = sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    const latestWeekStartDate = gridWeeks[0]?.startDate?.slice(0, 10) || '';
    const isPastWeekSelected = Boolean(
        filters.weekStartDate &&
        latestWeekStartDate &&
        filters.weekStartDate !== latestWeekStartDate
    );

    // ── Sort icon helper ─────────────────────────────────────────────────────
    const renderSortIcon = (column: string) => {
        if (["hours", "action", "description"].includes(column)) return null;
        // Disable sort icons if there are no records
        if (sortedData.length === 0) {
            return <span className="sort-icons disabled"><ArrowUpwardIcon className="sort-icon" /><ArrowDownwardIcon className="sort-icon" /></span>;
        }
        const isActive = sortColumn === column;
        return (
            <span className="sort-icons">
                <ArrowUpwardIcon className={`sort-icon ${isActive && sortDirection === 'asc' ? 'active' : ''}`} />
                <ArrowDownwardIcon className={`sort-icon ${isActive && sortDirection === 'desc' ? 'active' : ''}`} />
            </span>
        );
    };

    return (

        // ── (11) Header ──────────────────────────────────────────────  
        <div className="my-activity-container">
            <div className={`my-activity-header-row ${isPastWeekSelected ? 'with-helper-text' : ''}`}>
                <div className="my-activity-heading-block">
                    <h2 className="categories-heading">My Activity</h2>
                    {isPastWeekSelected && (
                        <div className="assign-week-helper-text" role="status" aria-live="polite">
                            <InfoOutlinedIcon className="assign-week-helper-icon" fontSize="small" />
                            <span>Assignments allowed only for current week</span>
                        </div>
                    )}
                </div>
                <div className="my-activity-filters">

                    {(() => {
                        // Disable if not benched or if week filter is set to a previous week
                        const disabled = !isUserBenched || isPastWeekSelected;
                        return (
                            <Button
                                variant="contained"
                                onClick={() => setAssignModalOpen(true)}
                                size="small"
                                className="btn-primary"
                                color={disabled ? 'inherit' : 'primary'}
                                disabled={disabled}
                                sx={disabled ? {
                                    backgroundColor: '#e0e0e0 !important',
                                    color: '#888 !important',
                                    borderColor: '#e0e0e0 !important',
                                    cursor: 'not-allowed !important',
                                    '&:hover': {
                                        backgroundColor: '#e0e0e0 !important',
                                        color: '#888 !important',
                                    },
                                } : {}}
                            >
                                <LocalIcon src="/icons/AddIcon.svg" className="btn-icon" />
                                Assign Activity
                            </Button>
                        );
                    })()}

                    <AssignActivityModal
                        open={assignModalOpen}
                        onClose={() => setAssignModalOpen(false)}
                        employeeId={null}
                        categories={categories}
                        technologies={technologies}
                        employees={employees}
                        activities={allActivities}
                        onAssign={async (payload: any) => {
                            const normalize = (name: string) => (name || "").toLowerCase().replace(/[^a-z0-9]/gi, "");
                            let targetWeekStartDate = filters.weekStartDate;
                            // Block assignment if week filter is not set
                           if (!targetWeekStartDate) {
    setBanner({
        type: "error",
        message: "Please select current week before assigning an activity."
    });

    return {
        error: true,
        message: "Assignment failed: No week selected.",
        specialError: true
    };
}
                            // If week filter is set, proceed as before
                            // --- Fetch ALL activities for ALL weeks for robust check ---
                            let allActivitiesAllWeeks: any[] = [];
                            try {
                                const resp = await apiGet(`/my-activity/GetMyActivity`);
                                let raw: any = [];
                                if (resp && typeof resp === 'object' && 'data' in resp) {
                                    raw = resp.data;
                                } else if (Array.isArray(resp)) {
                                    raw = resp;
                                }
                                const rows = Array.isArray(raw) ? raw : [];
                                allActivitiesAllWeeks = await Promise.all(
                                    rows.map(async (row: any) => {
                                        let activityDetails: any = {};
                                        try {
                                            activityDetails = await activityService.getActivityById(row.activityId);
                                        } catch {
                                            activityDetails = {};
                                        }
                                        return {
                                            category: activityDetails?.categoryName || "",
                                            categoryId: Number(activityDetails?.categoryId ?? row.categoryId ?? 0),
                                            technology: activityDetails?.technologyName || "",
                                            technologyId: Number(activityDetails?.technologyId ?? row.technologyId ?? 0),
                                            name: activityDetails?.name || "",
                                            description: activityDetails?.description || "",
                                            status: row.status,
                                            startDate: row.startDate,
                                            endDate: row.endDate,
                                            weekStartDate: row.weekStartDate,
                                            hours: row.totalHours ? row.totalHours.toFixed(2) : "",
                                            ...row,
                                        };
                                    })
                                );
                            } catch {}
                            // Check for duplicate activity in current week
                            const inputNorm = normalize(payload.name);
                            const currentWeekActivityNames = (allActivitiesAllWeeks || [])
                                .filter((a: any) => a.weekStartDate?.slice(0, 10) === targetWeekStartDate)
                                .map((a: any) => normalize(a.name));
                            if (currentWeekActivityNames.includes(inputNorm)) {
                                return { duplicate: true, message: "You already have this activity assigned for the current week." };
                            }
                            // Check for completed activity in previous week (robust)
                            const completedInPast = (allActivitiesAllWeeks || [])
                                .filter((a: any) =>
                                    normalize(a.name) === inputNorm &&
                                    a.status && String(a.status).toLowerCase() === 'completed' &&
                                    a.weekStartDate?.slice(0, 10) !== targetWeekStartDate
                                )
                                .sort((a: any, b: any) => new Date(b.weekStartDate).getTime() - new Date(a.weekStartDate).getTime());
                            if (completedInPast.length > 0) {
                                setReassignConfirm({
                                    open: true,
                                    week: completedInPast[0].weekStartDate,
                                    activity: completedInPast[0],
                                    payload,
                                    benchId: assigningBenchId
                                });
                                return { cancelled: true };
                            }
                            // --- Normal assignment logic below (unchanged) ---
                            let latestAllWeekActivities = allWeekActivities;
                            if (!latestAllWeekActivities || latestAllWeekActivities.length === 0) {
                                try {
                                    const allWeekResp = await apiGet(`/my-activity/GetMyActivity?weekStartDate=${encodeURIComponent(targetWeekStartDate)}`);
                                    let allWeekRaw: any = [];
                                    if (allWeekResp && typeof allWeekResp === 'object' && 'data' in allWeekResp) {
                                        allWeekRaw = allWeekResp.data;
                                    } else if (Array.isArray(allWeekResp)) {
                                        allWeekRaw = allWeekResp;
                                    }
                                    const allWeekRows = Array.isArray(allWeekRaw) ? allWeekRaw : [];
                                    latestAllWeekActivities = await Promise.all(
                                        allWeekRows.map(async (row: any) => {
                                            let activityDetails: any = {};
                                            try {
                                                activityDetails = await activityService.getActivityById(row.activityId);
                                            } catch {
                                                activityDetails = {};
                                            }
                                            return {
                                                category: activityDetails?.categoryName || "",
                                                categoryId: Number(activityDetails?.categoryId ?? row.categoryId ?? 0),
                                                technology: activityDetails?.technologyName || "",
                                                technologyId: Number(activityDetails?.technologyId ?? row.technologyId ?? 0),
                                                name: activityDetails?.name || "",
                                                description: activityDetails?.description || "",
                                                status: row.status,
                                                startDate: row.startDate,
                                                endDate: row.endDate,
                                                hours: row.totalHours ? row.totalHours.toFixed(2) : "",
                                                ...row,
                                            };
                                        })
                                    );
                                } catch {}
                            }
                            try {
                                let currentBenchId = assigningBenchId;
                                if (!currentBenchId) {
                                    const emptyRows = (latestAllWeekActivities || [])
                                        .filter((a: any) =>
                                            a.benchId &&
                                            a.weekStartDate?.slice(0, 10) === targetWeekStartDate &&
                                            (!a.category && !a.technology && !a.name && !a.status && !a.startDate && !a.endDate && !a.hours)
                                        )
                                        .sort((a: any, b: any) => Number(a.benchId) - Number(b.benchId));
                                    if (emptyRows.length > 0) {
                                        currentBenchId = emptyRows[0].benchId;
                                    } else {
                                        const weekRows = (latestAllWeekActivities || []).filter((a: any) => a.weekStartDate?.slice(0, 10) === targetWeekStartDate);
                                        currentBenchId = weekRows[0]?.benchId;
                                    }
                                }
                                // Try to infer employeeId from activities if possible
                                let employeeId = null;
                                if (!currentBenchId) {
                                    // Try to get employeeId from any activity row
                                    const anyRow = (activities || []).find((a: any) => a.employeeId);
                                    if (anyRow) employeeId = anyRow.employeeId;
                                }
                                // If still no valid benchId and no employeeId, block assignment and show error
                                if (!currentBenchId && !employeeId) {
                                    setBanner({ type: "error", message: "Assignment failed: Could not determine a valid bench or employee for assignment. Please select a week or contact support." });
                                    return { error: true, message: "Assignment failed: No valid benchId or employeeId found." };
                                }
                                // Prepare payload
                                const payloadData: any = {
                                    activityId: payload.activityId,
                                    name: payload.name,
                                    description: payload.description,
                                    status: "Yet to start",
                                    categoryId: payload.categoryId,
                                    technologyId: payload.technologyId,
                                    pointOfContactId: payload.pointOfContactId,
                                };
                                if (employeeId) payloadData.employeeId = employeeId;
                                await apiPut<any>(`/assign-activity/${currentBenchId || "0"}/assign`, payloadData);
                                setActivityVersion(v => v + 1);
                                try { if (currentBenchId) sessionStorage.setItem('pinnedBenchId', String(Number(currentBenchId))); } catch {}
                                setFilters(prev => ({
                                    ...prev,
                                    weekStartDate: targetWeekStartDate,
                                    category: null,
                                    technology: null,
                                    status: "",
                                    global: ""
                                }));
                                await fetchActivities();
                                try { window.dispatchEvent(new CustomEvent('benchUpdated')); } catch {}
                                setBanner({ type: "success", message: "Activity assigned successfully" });
                                return null;
                            } catch (err: any) {
                                return { error: true, message: "Assignment failed: " + (err && err.message ? err.message : String(err)) };
                            }
                        }}
                    />
                                {/* Confirmation Dialog for reassigning completed activity */}
                                <Dialog
                                    open={reassignConfirm.open}
                                    onClose={() => setReassignConfirm({ ...reassignConfirm, open: false })}
                                    maxWidth="sm"
                                    fullWidth
                                    PaperProps={{ className: 'reassign-confirm-dialog' }}
                                >
                                    <DialogTitle className="reassign-confirm-header">
                                        <div className="reassign-confirm-title-wrap">
                                            <span className="reassign-confirm-icon-wrap">
                                                <CheckCircleOutlineIcon className="reassign-confirm-icon" />
                                            </span>
                                            <span className="reassign-confirm-title">Activity Already Completed</span>
                                        </div>
                                        <IconButton
                                            size="small"
                                            onClick={() => setReassignConfirm({ ...reassignConfirm, open: false })}
                                            className="reassign-confirm-close"
                                            aria-label="Close"
                                        >
                                            <CloseIcon />
                                        </IconButton>
                                    </DialogTitle>
                                    <DialogContent>
                                        <div className="reassign-confirm-body">
                                            {(reassignConfirm.activity?.employeeName || reassignConfirm.activity?.empName || 'You')} has completed {(reassignConfirm.activity?.name || reassignConfirm.payload?.name || 'this activity')} on {reassignConfirm.week ? new Date(reassignConfirm.week).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : ''} week.<br />
                                            Do you want to assign it again for this week?
                                        </div>
                                    </DialogContent>
                                    <DialogActions className="reassign-confirm-actions">
                                        <Button onClick={async () => {
                                            // Proceed with assignment
                                            setReassignConfirm({ ...reassignConfirm, open: false });
                                            // Repeat assignment logic (safe, same as above)
                                            const payload = reassignConfirm.payload;
                                            let targetWeekStartDate = filters.weekStartDate;
                                            if (!targetWeekStartDate && gridWeeks.length > 0) {
                                                targetWeekStartDate = gridWeeks[0].startDate?.slice(0, 10) || "";
                                            }
                                            let currentBenchId = reassignConfirm.benchId;
                                            let latestAllWeekActivities = allWeekActivities;
                                            if (!currentBenchId) {
                                                const emptyRows = (latestAllWeekActivities || [])
                                                    .filter((a: any) =>
                                                        a.benchId &&
                                                        a.weekStartDate?.slice(0, 10) === targetWeekStartDate &&
                                                        (!a.category && !a.technology && !a.name && !a.status && !a.startDate && !a.endDate && !a.hours)
                                                    )
                                                    .sort((a: any, b: any) => Number(a.benchId) - Number(b.benchId));
                                                if (emptyRows.length > 0) {
                                                    currentBenchId = emptyRows[0].benchId;
                                                } else {
                                                    const weekRows = (latestAllWeekActivities || []).filter((a: any) => a.weekStartDate?.slice(0, 10) === targetWeekStartDate);
                                                    currentBenchId = weekRows[0]?.benchId;
                                                }
                                            }
                                            try {
                                                await apiPut<any>(`/assign-activity/${currentBenchId}/assign`, {
                                                    activityId: payload.activityId,
                                                    name: payload.name,
                                                    description: payload.description,
                                                    status: "Yet to start",
                                                    categoryId: payload.categoryId,
                                                    technologyId: payload.technologyId,
                                                    pointOfContactId: payload.pointOfContactId,
                                                });
                                                setActivityVersion(v => v + 1);
                                                try { if (currentBenchId) sessionStorage.setItem('pinnedBenchId', String(Number(currentBenchId))); } catch {}
                                                setFilters(prev => ({
                                                    ...prev,
                                                    weekStartDate: targetWeekStartDate,
                                                    category: null,
                                                    technology: null,
                                                    status: "",
                                                    global: ""
                                                }));
                                                await fetchActivities();
                                                try { window.dispatchEvent(new CustomEvent('benchUpdated')); } catch {}
                                                setBanner({ type: "success", message: "Activity assigned successfully" });
                                            } catch (err: any) {
                                                setBanner({ type: "error", message: "Assignment failed: " + (err && err.message ? err.message : String(err)) });
                                            }
                                        }} variant="contained" className="reassign-confirm-yes">Yes, Assign Anyway</Button>
                                        <Button
                                            onClick={() => {
                                                setReassignConfirm({ ...reassignConfirm, open: false });
                                                setAssignModalOpen(true);
                                            }}
                                            variant="outlined"
                                            className="reassign-confirm-no"
                                        >
                                            No, Cancel
                                        </Button>
                                    </DialogActions>
                                </Dialog>
                    <Autocomplete
                        size="small"
                        className="filter-week"
                        options={gridWeeks}
                        freeSolo
                        forcePopupIcon={true}
                        popupIcon={undefined}
                        getOptionLabel={(o) => typeof o === 'string' ? o : dateFormat(o?.startDate)}
                        isOptionEqualToValue={(option, value) =>
    option.id === value.id
}
                        value={
    gridWeeks.find(
        w => w.startDate?.slice(0, 10) === filters.weekStartDate
    ) || null
}
                        inputValue={weekInput}
                        onInputChange={(_, v) => {
    setWeekInput(v);

    if (!v) {
        handleFilterChange("weekStartDate", "");
    }
}}
                        onChange={(_, v) => {
                            if (typeof v === 'string') {
                                setWeekInput(v);
                                handleFilterChange("weekStartDate", "");
                            } else {
                                handleFilterChange("weekStartDate", v ? v.startDate?.slice(0, 10) : "");
                                setWeekInput(v ? dateFormat(v.startDate) : "");
                            }
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder="Week"
                                inputProps={{
                                    ...params.inputProps,
                                    style: {
                                        minWidth: 90,
                                        maxWidth: 180,
                                        width: 'auto',
                                        paddingRight: 32 // Add right padding for clear icon
                                    }
                                }}
                            />
                        )}
                    />
                    <Autocomplete
    size="small"
    className="my-activity-filter-category"
    options={gridCategories}
    forcePopupIcon={true}
    popupIcon={undefined}
    disableClearable
    getOptionLabel={(o) =>
        typeof o === "string" ? o : o.name
    }
    isOptionEqualToValue={(option, value) =>
        option.id === value.id
    }
    value={
        gridCategories.find(
            c => c.id === filters.category
        ) || null
    }
    inputValue={categoryInput}
    onInputChange={(_, v) => {

        setCategoryInput(v);

        if (!v) {
            handleFilterChange("category", null);
        }
    }}
    onChange={(_, newValue) => {

        if (typeof newValue === "string") {

            setCategoryInput(newValue);

            handleFilterChange("category", null);

        } else if (newValue) {

            handleFilterChange(
                "category",
                newValue.id
            );

            setCategoryInput(newValue.name);

        } else {

            handleFilterChange("category", null);

            setCategoryInput("");
        }
    }}
    clearOnEscape
    renderInput={(params) => {

        const { InputProps, ...rest } = params;

        return (
            <TextField
                {...rest}
                InputProps={{
                    ...InputProps,
                    endAdornment: (
                        <>
                            {(categoryInput || filters.category) && (
                                <InputAdornment position="end">
                                    <IconButton
                                        size="small"
                                        aria-label="clear category filter"
                                        onClick={() => {

                                            handleFilterChange("category", null);

                                            setCategoryInput("");

                                            setPage(0);
                                        }}
                                        edge="end"
                                    >
                                        <ClearIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            )}

                            {InputProps.endAdornment}
                        </>
                    )
                }}
                placeholder="Category"
            />
        );
    }}
/>
                   <Autocomplete
    size="small"
    className="my-activity-filter-technology"
    options={gridTechnologies}
    forcePopupIcon={true}
    popupIcon={undefined}
    disableClearable
    getOptionLabel={(o) =>
        typeof o === "string" ? o : o.name
    }
    isOptionEqualToValue={(option, value) =>
        option.id === value.id
    }
    value={
        gridTechnologies.find(
            t => t.id === filters.technology
        ) || null
    }
    inputValue={technologyInput}
    onInputChange={(_, v) => {

        setTechnologyInput(v);

        if (!v) {
            handleFilterChange("technology", null);
        }
    }}
    onChange={(_, newValue) => {

        if (typeof newValue === "string") {

            setTechnologyInput(newValue);

            handleFilterChange("technology", null);

        } else if (newValue) {

            handleFilterChange(
                "technology",
                newValue.id
            );

            setTechnologyInput(newValue.name);

        } else {

            handleFilterChange("technology", null);

            setTechnologyInput("");
        }
    }}
    clearOnEscape
    renderInput={(params) => {

        const { InputProps, ...rest } = params;

        return (
            <TextField
                {...rest}
                InputProps={{
                    ...InputProps,
                    endAdornment: (
                        <>
                            {(technologyInput || filters.technology) && (
                                <InputAdornment position="end">
                                    <IconButton
                                        size="small"
                                        aria-label="clear technology filter"
                                        onClick={() => {

                                            handleFilterChange("technology", null);

                                            setTechnologyInput("");

                                            setPage(0);
                                        }}
                                        edge="end"
                                    >
                                        <ClearIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            )}

                            {InputProps.endAdornment}
                        </>
                    )
                }}
                placeholder="Technology"
            />
        );
    }}
/>
    <Autocomplete
    size="small"
    className="my-activity-filter-status"
    options={gridStatuses.filter(
        s => !!s && String(s).trim() !== ""
    )}
    forcePopupIcon={true}
    popupIcon={undefined}
    disableClearable
    value={filters.status || null}
    inputValue={statusInput}
    onInputChange={(_, v) => {

        setStatusInput(v);

        if (!v) {
            handleFilterChange("status", "");
        }
    }}
    onChange={(_, newValue) => {

        if (typeof newValue === "string") {

            setStatusInput(newValue);

            handleFilterChange("status", newValue);

        } else if (newValue) {

            handleFilterChange("status", newValue);

            setStatusInput(newValue);

        } else {

            handleFilterChange("status", "");

            setStatusInput("");
        }
    }}
    clearOnEscape
    renderInput={(params) => {

        const { InputProps, ...rest } = params;

        return (
            <TextField
                {...rest}
                InputProps={{
                    ...InputProps,
                    endAdornment: (
                        <>
                            {(statusInput || filters.status) && (
                                <InputAdornment position="end">
                                    <IconButton
                                        size="small"
                                        aria-label="clear status filter"
                                        onClick={() => {

                                            handleFilterChange("status", "");

                                            setStatusInput("");

                                            setPage(0);
                                        }}
                                        edge="end"
                                    >
                                        <ClearIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            )}

                            {InputProps.endAdornment}
                        </>
                    )
                }}
                placeholder="Status"
            />
        );
    }}
/>
                    <TextField
                        placeholder="Search"
                        size="small"
                        className="my-activity-filter-search"
                        value={filters.global}
                        onChange={(e) => handleFilterChange("global", e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ fontSize: 16 }} />
                                </InputAdornment>
                            ),
                            endAdornment: (
                                filters.global ? (
                                    <InputAdornment position="end">
                                        <IconButton
                                            size="small"
                                            aria-label="clear search"
                                            onClick={() => handleFilterChange("global", "")}
                                            edge="end"
                                        >
                                            <ClearIcon fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                ) : null
                            ),
                        }}
                    />
                </div>
            </div>

            {/* ── (13) TABLE CARD ────────────────────────────────────────────── */}
            <div className={`my-activity-table-card ${isPastWeekSelected ? 'with-week-helper' : ''}`}>
                <div className="my-activity-table-scroll">
                    <table className="my-activity-table">
                        <thead>
                            <tr>
                                <th className="col-week" onClick={() => { if (sortColumn === 'weekStartDate') setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc'); setSortColumn('weekStartDate'); }}>
                                    Week {renderSortIcon('weekStartDate')}
                                </th>
                                <th className="col-category" onClick={() => { if (sortColumn === 'category') setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc'); setSortColumn('category'); }}>
                                    Category {renderSortIcon('category')}
                                </th>
                                <th className="col-technology" onClick={() => { if (sortColumn === 'technology') setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc'); setSortColumn('technology'); }}>
                                    Technology {renderSortIcon('technology')}
                                </th>
                                <th className="col-name" onClick={() => { if (sortColumn === 'name') setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc'); setSortColumn('name'); }}>
                                    Name {renderSortIcon('name')}
                                </th>
                                <th className="col-status" onClick={() => { if (sortColumn === 'status') setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc'); setSortColumn('status'); }}>
                                    Status {renderSortIcon('status')}
                                </th>
                                <th className="col-startDate" onClick={() => { if (sortColumn === 'startDate') setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc'); setSortColumn('startDate'); }}>
                                    Start Date {renderSortIcon('startDate')}
                                </th>
                                <th className="col-endDate" onClick={() => { if (sortColumn === 'endDate') setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc'); setSortColumn('endDate'); }}>
                                    End Date {renderSortIcon('endDate')}
                                </th>
                                <th className="col-hours">
                                    Hours {renderSortIcon('hours')}
                                </th>
                                <th className="col-action">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedData.length === 0 ? (
                                <tr className="my-activity-empty">
                                    <td colSpan={9} align="center">
                                        <div className="empty-content">
                                            {(() => {
                                                // If any filter is applied or a past week is selected, show search message
                                                const isPastWeek = filters.weekStartDate && weeks.length > 0 && filters.weekStartDate !== weeks[0].startDate?.slice(0, 10);
                                                if (
                                                    filters.global.trim() || filters.category || filters.technology || filters.status || isPastWeek
                                                ) {
                                                    // Special case: past week, not benched
                                                    if (isPastWeek && !isUserBenched) {
                                                        return <span className="th-inner">No activities were assigned to you in this week.</span>;
                                                    }
                                                    return <span className="th-inner">No activities found.<br/>Try a different search.</span>;
                                                }
                                                // Default: current week, not benched
                                                if (!isUserBenched) {
                                                    return <span className="th-inner">No activities are currently assigned.<br/>Please contact your manager to have an activity assigned.</span>;
                                                }
                                                return null;
                                            })()}
                                        </div>
                                    </td>
                                </tr>
                            ) : paginatedData.map((row: any, idx: number) => (
                                                                <tr key={idx}>
                                                                        <td className="col-week">{dateFormat(row.weekStartDate)}</td>
                                                                        <td className="col-category">
                                                                            {row.category && row.category.length > 20 ? (
                                                                                <Tooltip title={row.category} arrow>
                                                                                    <span style={{ display: 'inline-block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'middle', width: '100%' }}>
                                                                                        {row.category}
                                                                                    </span>
                                                                                </Tooltip>
                                                                            ) : (
                                                                                <span style={{ display: 'inline-block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'middle', width: '100%' }}>
                                                                                    {row.category}
                                                                                </span>
                                                                            )}
                                                                        </td>
                                                                        <td className="col-technology">{row.technology}</td>
                                                                        <td className="col-name">
                                                                            {row.name && row.name.length > 30 ? (
                                                                                <Tooltip title={row.name} arrow>
                                                                                    <span style={{ display: 'inline-block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'middle', width: '100%' }}>
                                                                                        {row.name}
                                                                                    </span>
                                                                                </Tooltip>
                                                                            ) : (
                                                                                <span style={{ display: 'inline-block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'middle', width: '100%' }}>
                                                                                    {row.name}
                                                                                </span>
                                                                            )}
                                                                        </td>
                                                                        <td className={`col-status ${row.status === 'Yet to start' ? 'status-yet-to-start' :
                                                                                row.status === 'In Progress' ? 'status-in-progress' :
                                                                                        row.status === 'Completed' ? 'status-completed' : ''
                                                                                }`}>{row.status}</td>

                                                                        <td className="col-startDate">{dateFormat(row.startDate)}</td>
                                                                        <td className="col-endDate">{dateFormat(row.endDate)}</td>
                                                                        <td className="col-hours">{(row.hours && Number(row.hours) !== 0) ? row.hours : ''}</td>
                                                                        <td className="col-action">
                                        {!row.engagementId ? (
                                            (() => {
                                                const isPastWeek = weeks.length > 0 && row.weekStartDate
                                                    ? new Date(row.weekStartDate).toDateString() !== new Date(weeks[0].startDate).toDateString()
                                                    : false;
                                                return (
                                                    <Tooltip title={isPastWeek ? 'Assignment disabled for past weeks' : 'Assign Activity'}>
                                                        <span>
                                                            <span style={{ opacity: isPastWeek ? 0.4 : 1, cursor: isPastWeek ? 'not-allowed' : 'pointer', display: 'inline-flex' }}>
                                                                <AssignActivityIcon onClick={() => {
                                                                    if (isPastWeek) return;
                                                                    setAssigningBenchId(Number(row.benchId) || null);
                                                                    setAssignModalOpen(true);
                                                                }} />
                                                            </span>
                                                        </span>
                                                    </Tooltip>
                                                );
                                            })()
                                        ) : row.status === 'Completed' ? (
                                            <Tooltip title="View Activity Tasks">
                                                <IconButton
                                                    size="small"
                                                    onClick={async () => {
                                                        let tasks: any[] = [];
                                                        try {
                                                            if (row.engagementId) {
                                                                tasks = await benchActivityService.getTasksByEngagementId(row.engagementId) as any[];
                                                                tasks = (tasks || []).map((t: any) => ({
                                                                    date: t.taskDate || t.date,
                                                                    comments: t.comments,
                                                                    hours: t.hours?.toString() || '',
                                                                }));
                                                            }
                                                        } catch { }
                                                        setViewModalTasks(tasks);
                                                        setViewModalActivityName(row.name);
                                                        setViewModalOpen(true);
                                                    }}
                                                >
                                                    <VisibilityIcon color="primary" style={{ fontSize: 16 }} />
                                                </IconButton>
                                            </Tooltip>
                                        ) : (
                                            <Tooltip title="Add Tasks">
                                                <IconButton
                                                    size="small"
                                                    onClick={async () => {
                                                        let tasks: any[] = [];
                                                        try {
                                                            if (row.engagementId) {
                                                                tasks = await benchActivityService.getTasksByEngagementId(row.engagementId) as any[];
                                                                tasks = (tasks || []).map((t: any) => ({
                                                                    taskId: t.taskID || t.taskId,
                                                                    date: t.taskDate || t.date,
                                                                    comments: t.comments,
                                                                    hours: t.hours?.toString() || '',
                                                                }));
                                                            }
                                                        } catch { }
                                                        setUpdateModalData({
                                                            activityId: row.activityId,
                                                            week: row.weekStartDate,
                                                            weekDisplay: row.weekStartDate ? new Date(row.weekStartDate).toLocaleDateString('en-CA') : '',
                                                            category: row.category,
                                                            technology: row.technology,
                                                            name: row.name,
                                                            description: row.description,
                                                            status:
                                                                row.status === 'Completed'
                                                                    ? 'completed'
                                                                    : row.status === 'Yet to start'
                                                                        ? 'yet to start'
                                                                        : 'In Progress',
                                                            tasks: tasks.length > 0
                                                                ? tasks
                                                                : [{
                                                                    date: row.startDate,
                                                                    comments: row.comments || row.description,
                                                                    hours: row.hours,
                                                                }],
                                                            engagementId: row.engagementId,
                                                        });
                                                        setUpdateModalOpen(true);
                                                    }}
                                                >
                                                    <TaskAltIcon color="primary" style={{ fontSize: 16 }} />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </td>
                                </tr>
                            ))}

                            {/* Modal for update activity */}
                            <UpdateActivityModal
                                open={updateModalOpen}
                                onClose={() => {
                                    setUpdateModalOpen(false);
                                    try {
                                        // @ts-ignore
                                        fetchActivities();
                                    } catch {
                                        window.location.reload();
                                    }
                                }}
                                activity={updateModalData || {
                                    week: '',
                                    category: '',
                                    technology: '',
                                    name: '',
                                    status: 'In Progress',
                                }}
                                initialRows={updateModalData?.tasks || []}
                                onSave={async (data: any) => {
                                    try {
                                        const engagementId = updateModalData?.engagementId || data.engagementId;
                                        if (!engagementId) throw new Error('Engagement ID missing');
                                        let taskDates: string[] = [];
                                        let totalHours = 0;
                                        if (Array.isArray(data.tasks)) {
                                            for (const row of data.tasks) {
                                                const task: MyActivityTaskDto = {
                                                    taskId: row.taskId,
                                                    taskDate: row.date ? (typeof row.date === 'string' ? row.date : row.date.format ? row.date.format('YYYY-MM-DD') : String(row.date)) : '',
                                                    comments: row.comments,
                                                    hours: row.hours,
                                                    status: row.status
                                                };
                                                if (task.taskId) {
                                                    await benchActivityService.updateTask(task.taskId, engagementId, task);
                                                } else {
                                                    await benchActivityService.addTask(engagementId, task);
                                                }
                                                if (task.taskDate) taskDates.push(task.taskDate);
                                                if (task.hours) totalHours += parseFloat(task.hours);
                                            }
                                        }
                                        let startDate = taskDates.length > 0 ? taskDates.reduce((min, curr) => curr < min ? curr : min, taskDates[0]) : null;
                                        let endDate = taskDates.length > 0 ? taskDates.reduce((max, curr) => curr > max ? curr : max, taskDates[0]) : null;
                                        await benchActivityService.updateMainActivity({
                                            activityId: updateModalData?.activityId,
                                            engagementId,
                                            name: updateModalData?.name || '',
                                            description: updateModalData?.description || '',
                                            status: data.status,
                                            endDate: typeof endDate === 'string' ? endDate : '',
                                            startDate: typeof startDate === 'string' ? startDate : '',
                                            totalHours
                                        });
                                        return true;
                                    } catch (err) {
                                        const errorMsg = (err && typeof err === 'object' && 'message' in err) ? (err as Error).message : String(err);
                                        alert('Failed to save activity task: ' + errorMsg);
                                        throw err;
                                    }
                                }}
                            />
                        </tbody>
                    </table>

                    <ViewActivityTasksModal
                        open={viewModalOpen}
                        onClose={() => setViewModalOpen(false)}
                        tasks={viewModalTasks}
                        activityName={viewModalActivityName}
                    />
                </div>

                {/* ── (22) PAGINATION ────────────────────────────────────────── */}
                {sortedData.length > 0 && (
                  <div className="my-activity-pagination">
                      <div className="pagination-info">
                          {" "}
                          {sortedData.length === 0
                              ? 0
                              : page * rowsPerPage + 1} {" "}
                          to{" "}
                          {Math.min((page + 1) * rowsPerPage, sortedData.length)}{" "}
                          of {sortedData.length}  | Page {page + 1} of {totalPages}
                      </div>
                      <button
                          className="page-btn first-btn"
                          disabled={page === 0}
                          onClick={() => setPage(0)}
                      >
                          First
                      </button>
                      <button
                          className="page-btn prev-btn"
                          disabled={isPrevDisabled}
                          onClick={() => setPage(Math.max(0, page - 1))}
                      >
                          Previous
                      </button>
                      <div className="page-numbers">
                          {(() => {
                              const blockSize = 5;
                              const currentBlock = Math.floor(page / blockSize);
                              const startPage = currentBlock * blockSize + 1;
                              const endPage = Math.min(startPage + blockSize - 1, totalPages);
                              const pageButtons = [];
                              for (let p = startPage; p <= endPage; p++) {
                                  pageButtons.push(
                                      <button
                                          key={p}
                                          className={`page-number ${p === page + 1 ? 'active' : ''}`}
                                          onClick={() => setPage(p - 1)}
                                      >
                                          {p}
                                      </button>
                                  );
                              }
                              return pageButtons;
                          })()}
                      </div>
                      <button
                          className="page-btn next-btn"
                          disabled={isNextDisabled}
                          onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                      >
                          Next
                      </button>
                      <button
                          className="page-btn last-btn"
                          disabled={page === totalPages - 1}
                          onClick={() => setPage(totalPages - 1)}
                      >
                          Last
                      </button>
                  </div>
                )}
            </div>
        {/* Toast Banner */}
{banner && (
    <div
        className={`toast-banner ${banner.type}`}
        style={
            banner.type === 'error' &&
            banner.message === 'Please select current week before assigning an activity.'
                ? {
                      background: '#fef3f2',
                      border: '1px solid #fecdca',
                      color: '#ff1c0c',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                  }
                : {}
        }
    >
        {banner.type === 'success' && (
            <span
                className="toast-icon"
                aria-hidden="true"
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <circle cx="10" cy="10" r="10" fill="#1b7f3c" />
                    <path
                        d="M6 10.5L9 13.5L15 7.5"
                        stroke="#fff"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            </span>
        )}

        {banner.type === 'error' &&
            banner.message === 'Please select current week before assigning an activity.' && (
                <span
                    className="toast-icon"
                    aria-hidden="true"
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <circle
                            cx="10"
                            cy="10"
                            r="9"
                            fill="#fff5f5"
                            stroke="#e53935"
                            strokeWidth="1.5"
                        />
                        <path
                            d="M10 5.5V10.5"
                            stroke="#e53935"
                            strokeWidth="2"
                            strokeLinecap="round"
                        />
                        <circle cx="10" cy="14" r="1" fill="#e53935" />
                    </svg>
                </span>
            )}

        <span
            className="toast-message"
            style={
                banner.type === 'error' &&
                banner.message === 'Please select current week before assigning an activity.'
                    ? {
                          color: '#e53935',
                          display: 'flex',
                          alignItems: 'center',
                          lineHeight: '20px',
                      }
                    : {}
            }
        >
            {banner.message}
        </span>

        <button
            className="toast-close"
            onClick={() => setBanner(null)}
            aria-label="Close"
            style={
                banner.type === 'error' &&
                banner.message === 'Please select current week before assigning an activity.'
                    ? {
                          color: '#e53935',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          lineHeight: 1,
                          marginLeft: 'auto',
                      }
                    : {}
            }
        >
            ×
        </button>
    </div>
)}
        </div>
    );
};

export default MyActivity;